import React, { useEffect, useRef, useState } from 'react'
import { Tabs, Tab, Checkbox } from '@mui/material'
import { Warning } from '@mui/icons-material'
import Skeleton from 'react-loading-skeleton'
import NoDataFound from '../../partials/NoDataFound'
import Api from '../../../_helpers/Api'
import { trans } from '../../../_providers/Translation'
import DateRange from '@/components/partials/DateRange'
import { useHistory, useLocation } from 'react-router'
import OptionsOnlineSales from './partials/OptionsOnlineSales'
import ChangeStatusDialog from './partials/ChangeStatusDialog'
import ConfirmOrderDialog from './partials/ConfirmOrderDialog'

function OnlineSalesTable() {
    const history = useHistory()
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const selected = searchParams.get('id') ? Number(searchParams.get('id')) : null

    const searchTimeoutRef = useRef(null)

    const INITIAL_FILTERS = {
        search: '',
        status: '',
        payment: '',
        date_from: '',
        date_to: '',
        tab: 'today'
    }

    const [loading, setLoading] = useState(true)
    const [orders, setOrders] = useState([])
    const [statusDialogOrder, setStatusDialogOrder] = useState(null)
    const [confirmDialogOrderId, setConfirmDialogOrderId] = useState(null)
    const [state, setState] = useState({
        loading: true,
        data: [],
        filters: INITIAL_FILTERS
    })

    const getToday = () => {
        const today = new Date()
        const yyyy = today.getFullYear()
        const mm = String(today.getMonth() + 1).padStart(2, '0')
        const dd = String(today.getDate()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd}`
    }

    useEffect(() => {
        setState(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                search: searchParams.get('search') || '',
                status: searchParams.get('status') || '',
                payment: searchParams.get('payment') || '',
                date_from: searchParams.get('date_from') || '',
                date_to: searchParams.get('date_to') || '',
                tab: searchParams.get('tab') || 'today'
            },
            update: new Date().getTime()
        }))
    }, [location.search])

    useEffect(() => {
        if (state.setFilter) {
            searchParams.set('page', state.page)

            Object.entries(state.filters).map(filter => {
                searchParams.set(filter[0], filter[1])
            })

            history.push('?' + searchParams.toString())
        }
    }, [state.setFilter])

    useEffect(() => {
        if (state.update) {
            setLoading(true)

            const params = [
                `tab=${state.filters.tab === "today" ? 0 : 1}`,
                `search=${state.filters.search}`,
                `status=${state.filters.status}`,
                `payment=${state.filters.payment}`
            ]
            if (state.filters.tab === 'today') {
                const today = getToday()
                params.push(`date_from=${today}`)
                params.push(`date_to=${today}`)
            } else {
                if (state.filters.date_from) params.push(`date_from=${state.filters.date_from}`)
                if (state.filters.date_to) params.push(`date_to=${state.filters.date_to}`)
            }
            Api.get(`sales_orders?${params.join('&')}`)
                .then(res => {
                    setOrders(res.data || [])
                    setLoading(false)
                })
                .catch(() => {
                    setOrders([])
                    setLoading(false)
                })
        }
    }, [state.update])

    const reloadOrders = () => {
        setLoading(true)
        const params = [
            `tab=${state.filters.tab === "today" ? 0 : 1}`,
            `search=${state.filters.search}`,
            `status=${state.filters.status}`,
            `payment=${state.filters.payment}`
        ]
        if (state.filters.date_from) params.push(`date_from=${state.filters.date_from}`)
        if (state.filters.date_to) params.push(`date_to=${state.filters.date_to}`)
        Api.get(`sales_orders?${params.join('&')}`)
            .then(res => {
                setOrders(res.data || [])
                setLoading(false)
            })
            .catch(() => {
                setOrders([])
                setLoading(false)
            })
    }

    const handleConfirm = (id) => {
        setConfirmDialogOrderId(id)
    }

    const handleReject = async (id) => {
        try {
            await Api.post(`/sales_orders/${id}/reject`)
            reloadOrders()
        } catch (e) {
            // Optionally handle error
        }
    }

    const handleChangeStatus = (id) => {
        const order = orders.find(o => o.id === id)
        if (order) setStatusDialogOrder(order)
    }

    const statusKeys = ['pending', 'approved', 'processing', 'shipped', 'delivered', 'cancelled']
    const statusOptions = statusKeys.map(key => ({
        value: key,
        label: trans(`onlineSales.orderStatus.${key}`)
    }))
    const paymentOptions = [
        { value: '', label: trans('tables.payment') },
        { value: 'paid', label: trans('tables.paid') },
        { value: 'unpaid', label: trans('tables.unpaid') },
        { value: 'googlepay', label: 'Google Pay' },
    ]

    const handleToggleRow = rowId => {

        document.querySelectorAll('.collapsable-table tr.active').forEach(el => {
            el.classList.remove('active')
        })

        let rows = document.querySelectorAll(`tr[data-row="${rowId}"]`)

        for (let i = 0; i < rows.length; i++) {
            if (rows[i].classList.contains('active')) {
                rows[i].classList.remove('active')
            } else {
                rows[i].classList.add('active')
            }
        }

        searchParams.set('id', rowId)

        history.replace('?' + searchParams.toString())

    }

    const handleClearFilters = (moveToTab = null) => {
        setState(prev => ({
            ...prev,
            filters: {
                ...INITIAL_FILTERS,
                tab: moveToTab !== null ? moveToTab : INITIAL_FILTERS.tab
            },
            setFilter: new Date().getTime()
        }))
    }


    function handleSearch(e) {
        const { name, value } = e.target

        clearTimeout(searchTimeoutRef.current)

        setState(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                [name]: value
            },
        }))

        searchTimeoutRef.current = setTimeout(() => {
            setState(prev => ({
                ...prev,
                page: 1,
                setFilter: new Date().getTime()
            }))
        }, 300)
    }

    return (
        <div id="storage" className="wrapper">
            <div className="top-part">
                <div className="left">
                    <h1 className="page-heading">
                        {trans('onlineSales.heading')}
                    </h1>
                    <p className="page-subheading">
                        {trans('onlineSales.subheading')}
                    </p>
                </div>
            </div>
            <Tabs
                value={state.filters.tab}
                onChange={(e, v) => {
                    handleClearFilters(v)
                }}
                indicatorColor="primary"
                textColor="primary"
                style={{ marginBottom: 16 }}
            >
                <Tab value="today" label={trans('onlineSales.tabs.today')} />
                <Tab value="all" label={trans('onlineSales.tabs.all')} />
            </Tabs>
            <div className='pannel'>

                <form className="filter-standard">
                    <div className='col'>
                        <input
                            type="search"
                            placeholder={trans('tables.search')}
                            value={state.filters.search}
                            name="search"
                            onChange={handleSearch}
                        />
                    </div>
                    <div className='col'>
                        <select
                            value={state.filters.status}
                            onChange={handleSearch}
                            name="status"
                        >
                            {statusOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className='col'>
                        <select
                            value={state.filters.payment}
                            onChange={handleSearch}
                            name="payment"
                        >
                            {paymentOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    {state.filters.tab !== 'today' ?
                        <div className='col'>
                            <DateRange
                                startDate={state.filters.date_from}
                                endDate={state.filters.date_to}
                                onStartDateChange={(date) => {
                                    handleSearch({
                                        target: {
                                            name: 'date_from',
                                            value: date
                                        }
                                    })
                                }}
                                onEndDateChange={(date) => {
                                    handleSearch({
                                        target: {
                                            name: 'date_to',
                                            value: date
                                        }
                                    })
                                }}
                            />
                        </div>
                        :
                        <>
                        </>
                    }
                    <div className='col'>
                        <button type='button' className='button-clear-filter' onClick={handleClearFilters}>
                            Изчистване
                        </button>
                    </div>
                </form>
                <hr />
                {loading ? (
                    <Skeleton height={45} count={10} />
                ) : orders.length === 0 ? (
                    <NoDataFound />
                ) : (
                    <div className='table-scroller'>
                        <table className="collapsable-table">
                            <thead>
                                <tr>
                                    <th padding="checkbox">
                                        <Checkbox color="primary" size='small' />
                                    </th>
                                    <th>№</th>
                                    <th>{trans('tables.date')}</th>
                                    <th>{trans('tables.customer')}</th>
                                    <th>{trans('tables.address')}</th>
                                    <th>{trans('tables.total')}</th>
                                    <th>{trans('tables.payment')}</th>
                                    <th>{trans('tables.status')}</th>
                                    <th className="options">{trans('tables.options')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => (
                                    <React.Fragment key={order.id}>
                                        <tr
                                            data-row={order.id}
                                            className={Number(order.id) === Number(selected) ? 'active' : ''}
                                            onClick={() => handleToggleRow(order.id)}
                                        >
                                            <td padding="checkbox">
                                                <Checkbox color="primary" size='small' />
                                            </td>
                                            <td>{order.order_number}</td>
                                            <td>{order.created_at ? new Date(order.created_at).toLocaleString() : ''}</td>
                                            <td>{order.customer ? order.customer.name + ' ' + order.customer.lastname : ''}</td>
                                            <td>{order.delivery_address_line1 + ' ' + (order.delivery_address_line2 ?? '') + ' ' + order.delivery_city + ' ' + order.delivery_postcode}</td>
                                            <td style={{ fontWeight: 700 }}>
                                                {order.total_amount}
                                            </td>
                                            <td style={{ color: order.payment_status === 'unpaid' ? '#e57373' : '#333' }}>
                                                {order.payment_method}
                                            </td>
                                            <td style={{ color: order.status === 'cancelled' ? '#fbc02d' : '#388e3c', fontWeight: 500 }}>
                                                {order.status === 'cancelled' ? <Warning style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 4 }} /> : null}
                                                {trans(`onlineSales.orderStatus.${order.status}`) || order.status}
                                            </td>

                                            <td>
                                                <OptionsOnlineSales
                                                    id={order.id}
                                                    status={order.status}
                                                    handleConfirm={handleConfirm}
                                                    handleReject={handleReject}
                                                    handleChangeStatus={handleChangeStatus}
                                                />
                                            </td>
                                        </tr>
                                        <tr
                                            className={['collapsable', Number(order.id) === Number(selected) ? 'active' : ''].join(' ')}
                                            data-row={order.id}
                                        >
                                            <td colSpan="9">
                                                <div className='pannel'>
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>ID</th>
                                                                <th>{trans('tables.type')}</th>
                                                                <th>{trans('tables.name')}</th>
                                                                <th>{trans('tables.quantity')}</th>
                                                                <th>{trans('tables.unitPrice')}</th>
                                                                <th>{trans('tables.total')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(order.items || []).map(item => (
                                                                <tr
                                                                    key={item.id}
                                                                >
                                                                    <td
                                                                        style={{
                                                                            cursor: "default"
                                                                        }}
                                                                    >
                                                                        {item.id}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            cursor: "default"
                                                                        }}
                                                                    >
                                                                        {item.product.type || ''}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            cursor: "default"
                                                                        }}
                                                                    >
                                                                        {item.product.name || ''}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            cursor: "default"
                                                                        }}
                                                                    >
                                                                        {item.quantity}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            cursor: "default"
                                                                        }}
                                                                    >
                                                                        {item.unit_price}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            cursor: "default"
                                                                        }}
                                                                    >
                                                                        {item.total_price}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {statusDialogOrder &&
                    <ChangeStatusDialog
                        order={statusDialogOrder}
                        onClose={() => setStatusDialogOrder(null)}
                        onSaved={reloadOrders}
                    />
                }

                {confirmDialogOrderId &&
                    <ConfirmOrderDialog
                        orderId={confirmDialogOrderId}
                        onClose={() => setConfirmDialogOrderId(null)}
                        onSaved={reloadOrders}
                    />
                }
            </div>
        </div >
    )
}
export default OnlineSalesTable
